import React, { useState, useEffect } from 'react';
import { fetchCategories, fetchSubcategories } from '../services/api';
import { toast } from 'react-toastify';

const ContestFilter = ({ onFilterChange, filters }) => {
  const [heading, setHeading] = useState(filters.heading || '');
  const [companyName, setCompanyName] = useState(filters.companyName || '');
  const [category, setCategory] = useState(filters.category || '');
  const [subcategory, setSubcategory] = useState(filters.subcategory || '');
  const [activeStatus, setActiveStatus] = useState(filters.isActive === 'true' ? 'active' : filters.isActive === 'false' ? 'inactive' : 'all');
  
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Track selected category ID separately from the category name
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  
  // Mobile filter collapse state
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Fetch categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const data = await fetchCategories();
        setCategories(data);
        
        // If we have a category filter already applied, find its ID
        if (category) {
          const categoryObj = data.find(cat => cat.name === category);
          if (categoryObj) {
            setSelectedCategoryId(categoryObj.id.toString());
            // Load the subcategories for this category
            await loadSubcategoriesForCategory(categoryObj.id);
          }
        }
      } catch (error) {
        toast.error('Failed to fetch categories');
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, [category]);

  // Function to load subcategories for a specific category ID
  const loadSubcategoriesForCategory = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchSubcategories(categoryId);
      setSubcategories(data || []);
    } catch (error) {
      toast.error('Failed to fetch subcategories');
      setSubcategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare filter params based on activeStatus
    const filterParams = {
      heading,
      companyName,
      category,
      subcategory
    };
    
    // Only add isActive parameter if not "all"
    if (activeStatus === 'active') {
      filterParams.isActive = 'true';
    } else if (activeStatus === 'inactive') {
      filterParams.isActive = 'false';
    }
    
    onFilterChange(filterParams);
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategoryId(categoryId);
    
    if (categoryId) {
      // Find the category name from the selected ID
      const categoryObj = categories.find(cat => cat.id.toString() === categoryId);
      if (categoryObj) {
        setCategory(categoryObj.name);
        // Load subcategories for this category
        loadSubcategoriesForCategory(categoryId);
      } else {
        setCategory('');
      }
    } else {
      setCategory('');
      setSubcategories([]);
    }
    
    // Reset subcategory when category changes
    setSubcategory('');
  };

  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    
    if (subcategoryId) {
      // Find the subcategory name from the selected ID
      const subcategoryObj = subcategories.find(subcat => subcat.id.toString() === subcategoryId);
      if (subcategoryObj) {
        setSubcategory(subcategoryObj.name);
      } else {
        setSubcategory('');
      }
    } else {
      setSubcategory('');
    }
  };

  const handleReset = () => {
    setHeading('');
    setCompanyName('');
    setCategory('');
    setSelectedCategoryId('');
    setSubcategory('');
    setActiveStatus('all');
    
    onFilterChange({
      heading: '',
      companyName: '',
      category: '',
      subcategory: ''
      // No isActive parameter for "all"
    });
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      {/* Mobile Filter Toggle Button */}
      <div className="md:hidden border-b border-gray-200">
        <button
          type="button"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
        >
          <span className="text-lg font-semibold text-gray-900">Filter Contests</span>
          <svg
            className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isFilterOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Filter Contests</h2>
      </div>

      {/* Filter Form */}
      <div className={`${isFilterOpen ? 'block' : 'hidden'} md:block p-4`}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Heading Filter */}
          <div>
            <label htmlFor="heading" className="block text-sm font-medium text-gray-700">
            Contest Name
            </label>
            <input
              type="text"
              id="heading"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Search by heading"
            />
          </div>

          {/* Company Name Filter */}
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
              Sponsor Name
            </label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Search by sponsor name"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              id="category"
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              disabled={isLoading}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subcategory Filter */}
          <div>
            <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700">
              Subcategory
            </label>
            <select
              id="subcategory"
              value={subcategories.find(s => s.name === subcategory)?.id || ''}
              onChange={handleSubcategoryChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              disabled={!selectedCategoryId || isLoading}
            >
              <option value="">All Subcategories</option>
              {subcategories.map((subcat) => (
                <option key={subcat.id} value={subcat.id}>
                  {subcat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Active Status Filter - Dropdown replacing checkbox */}
          <div>
            <label htmlFor="activeStatus" className="block text-sm font-medium text-gray-700">
              Contest Status
            </label>
            <select
              id="activeStatus"
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Contests</option>
              <option value="active">Active Contests</option>
              <option value="inactive">Inactive Contests</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Reset
          </button>
          <button
            type="submit"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default ContestFilter; 